import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, MapPin, CurrencyGbp, Users, ArrowRight, Car, ShieldCheck } from "@phosphor-icons/react";

export default function PublicJob() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    fetch(`${API}/public/jobs/${id}`)
      .then(async (r) => { if (r.ok) setData(await r.json()); else setErr((await r.json()).detail || "Not available"); })
      .catch(() => setErr("Not available"));
  }, [id]);

  if (err) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <h1 className="font-display text-3xl font-extrabold text-slate-900">{err}</h1>
        <p className="text-slate-600 mt-2">This shift may have been paused or filled. Explore other jobs below.</p>
        <Link to="/register?role=staff"><Button className="mt-4 bg-blue-600 hover:bg-blue-700">Browse jobs</Button></Link>
      </div>
    </div>
  );
  if (!data) return <div className="p-10 label-caps">Loading…</div>;
  const { job, branding } = data;
  const brand = branding?.hero_color || "#0F172A";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl font-extrabold text-slate-900">Shift<span className="text-blue-600">Flex</span></Link>
          <Link to="/register?role=staff"><Button data-testid="public-cta-signup" className="bg-blue-600 hover:bg-blue-700">Sign up to book <ArrowRight size={14} className="ml-1"/></Button></Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="card-tactile overflow-hidden" data-testid="public-job-card">
          <div className="h-24 relative" style={{ background: `linear-gradient(135deg, ${brand}, ${brand}CC)` }}>
            {branding?.logo && (
              <div className="absolute bottom-3 left-4 w-16 h-16 bg-white rounded-md border-2 border-white shadow overflow-hidden">
                <img src={fileUrl(branding.logo)} alt="brand" className="w-full h-full object-contain"/>
              </div>
            )}
            <div className={`absolute bottom-3 ${branding?.logo ? "left-24" : "left-5"} text-white`}>
              <div className="font-display text-lg font-extrabold">{branding?.company_name || job.company_name}</div>
              <div className="text-white/80 text-xs">Public shift listing</div>
            </div>
          </div>
          <div className="p-6">
            <div className="label-caps text-blue-600 mb-2">{job.category}</div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">{job.title}</h1>
            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
              <Row icon={Calendar} label="Date" value={job.date} />
              <Row icon={Clock} label="Time" value={`${job.start_time}–${job.end_time}`} />
              <Row icon={MapPin} label="Location" value={`${job.location}${job.postcode ? " · " + job.postcode : ""}`} />
              <Row icon={CurrencyGbp} label="Pay rate" value={`£${job.pay_rate}/hr`} />
              <Row icon={Users} label="Positions" value={`${job.positions_remaining} left of ${job.staff_required}`} />
              {job.vehicle_required && <Row icon={Car} label="Vehicle" value={`${job.vehicle_type || "Required"}${job.mileage_rate ? ` · £${job.mileage_rate}/mi` : ""}`} />}
            </div>
            <div className="mt-5">
              <div className="label-caps mb-1">Description</div>
              <p className="text-slate-800 leading-relaxed">{job.description}</p>
            </div>
            {(job.required_skills?.length || 0) > 0 && (
              <div className="mt-4">
                <div className="label-caps mb-2">Required skills</div>
                <div className="flex flex-wrap gap-2">
                  {job.required_skills.map((s) => <span key={s} className="px-2.5 py-1 bg-slate-100 rounded-md text-sm font-semibold">{s}</span>)}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card-tactile p-6 bg-slate-900 text-white flex items-center justify-between gap-4 flex-wrap" data-testid="public-cta-block">
          <div>
            <div className="font-display text-xl font-bold">Want to book this shift?</div>
            <div className="text-slate-300 text-sm mt-1">Sign up as staff — takes 2 minutes.</div>
          </div>
          <Link to="/register?role=staff"><Button className="bg-blue-600 hover:bg-blue-700 h-11 px-6">Sign up & book <ArrowRight size={14} className="ml-1"/></Button></Link>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500 justify-center">
          <ShieldCheck size={14}/> Every ShiftFlex worker is document-verified.
        </div>
      </main>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5 p-3 bg-slate-50 rounded-md">
      <Icon size={18} className="text-blue-600 mt-0.5" />
      <div>
        <div className="label-caps">{label}</div>
        <div className="text-slate-900 font-semibold">{value}</div>
      </div>
    </div>
  );
}
